<template>
  <div
    class="login-page"
    :class="[isDark ? 'theme-dark' : 'theme-light', currentLang === 'en' ? 'ltr-mode' : '']"
    :dir="currentLang === 'en' ? 'ltr' : 'rtl'"
    :lang="currentLang"
  >
    <NavbarStyledRegister v-model:isDark="isDark" v-model:selectedLanguage="currentLang" />
    <div
      class="login-container"
    >
      <div class="login-form-col">
        <div class="login-card">
          <div class="brand-mark">
            <span class="brand-dot"></span>
          </div>

          <h2>{{ $t('auth.loginPage.welcome') }}</h2>
          <p class="subtitle">{{ $t('auth.loginPage.continue') }}</p>

          <!-- Role Selection Modal -->
          <div v-if="showRoleSelection" class="role-selection-modal">
            <div class="role-selection-content">
              <h3>{{ $t('auth.loginPage.selectRole') }}</h3>
              <p>{{ $t('auth.loginPage.selectRoleMessage') }}</p>

              <div class="role-options">
                <button
                  v-for="role in availableRoles"
                  :key="role"
                  class="role-option-btn"
                  @click="selectRole(role)"
                >
                  {{ $t(`auth.roles.${role}`) }}
                </button>
              </div>

              <button class="btn-secondary" @click="showRoleSelection = false">
                {{ $t('common.cancel') }}
              </button>
            </div>
          </div>

          <!-- Company Selection Modal -->
          <div v-if="showCompanySelection" class="company-selection-modal">
            <div class="company-selection-content">
              <div v-if="parentSubcodeSelectionOpen" class="parent-subcode-overlay">
                <section class="parent-subcode-dialog" role="dialog" aria-modal="true" aria-labelledby="parent-subcode-title">
                  <h3 id="parent-subcode-title">انتخاب حساب والد</h3>
                  <p>برای این شماره چند حساب پیدا شد. کد موردنظر را انتخاب کنید.</p>
                  <label v-for="parent in parentSubcodeMatches" :key="parent.id_subcode" class="parent-subcode-option">
                    <input v-model="selectedParentSubcode" type="radio" :value="String(parent.id_subcode)">
                    <span><strong>{{ parent.title || 'بدون نام' }}</strong><small>کد: {{ parent.id_subcode }}</small><small v-if="parent.address && parent.address !== '-'">{{ parent.address }}</small></span>
                  </label>
                  <div class="parent-subcode-actions">
                    <button type="button" class="btn-secondary" @click="cancelParentSubcodeSelection">انصراف</button>
                    <button type="button" class="btn-primary" :disabled="!selectedParentSubcode || loadingCompanies" @click="continueWithParentSubcode">
                      {{ loadingCompanies ? 'در حال بررسی…' : 'ادامه' }}
                    </button>
                  </div>
                </section>
              </div>
              <h3>{{ $t('auth.loginPage.selectCompany') }}</h3>
              <p>{{ $t('auth.loginPage.selectCompanyMessage') }}</p>
              <p v-if="errorMessage" class="text-danger" role="alert">{{ errorMessage }}</p>

              <div v-if="registrationCheckLoading" class="registration-check-message" role="status">
                در حال بررسی تکمیل اطلاعات حساب...
              </div>
              <div v-else-if="registrationCheckFailed" class="registration-check-message error" role="alert">
                {{ registrationCheckError }}
                <button type="button" class="btn-secondary" @click="retryRegistrationCheck">
                  تلاش دوباره
                </button>
              </div>

              <!-- Authenticated Companies -->
              <div v-if="authenticatedCompanies.length" class="company-section">
                <h4 class="company-section-title">
                  {{ $t('auth.loginPage.authenticatedCompanies') }}
                </h4>

                <div class="company-options">
                  <div
                    v-for="company in authenticatedCompanies"
                    :key="company.company_id"
                    class="company-option authenticated"
                    @click="selectCompany(company)"
                  >
                    <div class="company-info">
                      <h4>{{ company.company_name }}</h4>
                      <p v-if="company.description" class="company-description">
                        {{ company.description }}
                      </p>
                      <span class="company-status-badge authenticated">
                        {{ $t('auth.loginPage.authenticated') }}
                      </span>
                    </div>

                    <button class="btn-primary btn-sm" :disabled="registrationCheckLoading || registrationCheckFailed">
                      {{ $t('common.select') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Not Authenticated Companies -->
              <div v-if="notAuthenticatedCompanies.length" class="company-section">
                <h4 class="company-section-title">
                  {{ $t('auth.loginPage.notAuthenticatedCompanies') }}
                </h4>

                <p class="company-section-subtitle">
                  {{ $t('auth.loginPage.notAuthenticatedMessage') }}
                </p>

                <div class="company-options">
                  <div
                    v-for="company in notAuthenticatedCompanies"
                    :key="company.company_id"
                    class="company-option not-authenticated"
                    @click="registerCompany(company)"
                  >
                    <div class="company-info">
                      <h4>{{ company.company_name }}</h4>
                      <p v-if="company.description" class="company-description">
                        {{ company.description }}
                      </p>
                      <span class="company-status-badge not-authenticated">
                        {{ $t('auth.loginPage.notRegistered') }}
                      </span>
                    </div>

                    <button class="btn-warning btn-sm" :disabled="registrationCheckLoading || registrationCheckFailed">
                      {{ $t('common.register') }}
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="availableCompanies.length === 0" class="no-companies">
                {{ $t('auth.loginPage.noCompanies') }}
              </div>

              <button class="btn-secondary" @click="logoutAndReset">
                {{ $t('common.logout') }}
              </button>
            </div>
          </div>

          <!-- Login Form -->
          <form
            v-if="!showRoleSelection && !showCompanySelection"
            @submit.prevent="handleLogin"
          >
            <div v-if="errorMessage" class="text-danger">{{ errorMessage }}</div>
            <div v-if="successMessage" class="text-success">{{ successMessage }}</div>

            <div class="form-group">
              <label class="form-label">
                {{ $t('auth.loginPage.phone') }}
              </label>

              <input
                v-model="loginData.phoneNumber"
                class="form-input1"
                :placeholder="$t('auth.loginPage.phonePlaceholder')"
                required
              />

              <span v-if="fieldErrors.phoneNumber" class="text-danger">
                {{ fieldErrors.phoneNumber }}
              </span>
            </div>

            <div class="form-group password-group">
              <label class="form-label">
                {{ $t('auth.loginPage.password') }}
              </label>
              
              <div class="input-wrapper">
                <input
                  v-model="loginData.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input1"
                  :placeholder="$t('auth.loginPage.passwordPlaceholder')"
                  required
                />
                <button 
                  type="button" 
                  class="toggle-password" 
                  @click="showPassword = !showPassword"
                  aria-label="Toggle password visibility"
                >
                  <!-- آیکون چشم (SVG) -->
                  <svg v-if="!showPassword" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>

              <span v-if="fieldErrors.password" class="text-danger">
                {{ fieldErrors.password }}
              </span>
            </div>

            <button type="submit" class="login-btn" :disabled="loading">
              {{ loading ? $t('auth.loading') : $t('auth.login') }}
            </button>

            <div class="forgot-password-link">
              <router-link to="/reset-password">
                {{ $t('auth.loginPage.forgotPassword') }}
              </router-link>
            </div>
            <div
              class="register-link"
              v-if="!showRoleSelection && !showCompanySelection"
            >
              {{ $t('auth.loginPage.noAccount') }}
              <router-link to="/register">
                {{ $t('auth.register') }}
              </router-link>
            </div>
          </form>

        </div>
      </div>

      <div class="register-image-col" aria-hidden="true">
        <div class="visual">
          <div class="hero-orbit">
            <img class="visual" :src="visual" alt="" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/api'
import visual from '@/assets/register-login-bg.svg'
import NavbarStyledRegister from '@/components/navbar_styled_register.vue'

export default {
  name: 'LoginCustomer',
  data() {
    return {
      showPassword: false,
      currentLang: 'fa',
      isDark: localStorage.getItem('theme') === 'dark',
      visual,
      loading: false,
      loadingCompanies: false,
      errorMessage: '',
      successMessage: '',
      showRoleSelection: false,
      showCompanySelection: false,
      parentSubcodeSelectionOpen: false,
      parentSubcodeMatches: [],
      selectedParentSubcode: '',
      pendingParentCompany: null,
      showCustomerRegistrationModal: false,
      customerRegistrationProfile: null,
      pendingLoginData: null,
      registrationCheckLoading: false,
      registrationCheckFailed: false,
      registrationCheckError: '',
      availableRoles: [],
      loginData: {
        phoneNumber: '',
        password: ''
      },
      fieldErrors: {
        phoneNumber: '',
        password: ''
      },
      translations: {
        en: {
          welcome: "Welcome",
          continueToAccount: "Continue to your account",
          phoneNumber: "Phone Number",
          phoneNumberPlaceholder: "Enter your phone number",
          password: "Password",
          passwordPlaceholder: "Enter your password",
          login: "Login",
          loggingIn: "Logging in",
          forgotPassword: "Forgot your password?",
          noAccount: "Don't have an account? ",
          register: "Register",
          selectRole: "Select Role",
          selectRoleMessage: "You have access to multiple dashboards. Please select which one you want to access:",
          customer: "Customer Dashboard",
          employer: "Employer Dashboard",
          admin: "Admin Dashboard",
          cancel: "Cancel",
          selectCompany: "Select Company",
          selectCompanyMessage: "Choose a company to continue",
          authenticatedCompanies: "Authenticated Companies",
          notAuthenticatedCompanies: "Companies Not Registered",
          notAuthenticatedMessage: "Your password doesn't match in these companies. Register to sync your password.",
          authenticated: "Authenticated",
          notRegistered: "Not Registered",
          select: "Select",
          noCompanies: "No companies available",
          logout: "Logout"
        },
        fa: {
          welcome: "خوش آمدید",
          continueToAccount: "برای ورود به حساب کاربری خود ادامه دهید",
          phoneNumber: "شماره موبایل",
          phoneNumberPlaceholder: "شماره موبایل خود را وارد کنید",
          password: "رمز عبور",
          passwordPlaceholder: "رمز عبور خود را وارد کنید",
          login: "ورود",
          loggingIn: "در حال ورود",
          forgotPassword: "رمز عبور خود را فراموش کرده اید؟",
          noAccount: "حساب کاربری ندارید؟ ",
          register: "ثبت نام کنید",
          selectRole: "انتخاب نقش",
          selectRoleMessage: "شما دسترسی به چندین پنل دارید. لطفا انتخاب کنید به کدام پنل می‌خواهید وارد شوید:",
          customer: "پنل مشتری",
          employer: "پنل مدیریت",
          admin: "پنل مدیر",
          cancel: "انصراف",
          selectCompany: "انتخاب شرکت",
          selectCompanyMessage: "یک شرکت را برای ادامه انتخاب کنید",
          authenticatedCompanies: "شرکت‌های تاییدشده",
          notAuthenticatedCompanies: "شرکت‌های ثبت‌نشده",
          notAuthenticatedMessage: "رمز عبور شما در این شرکت‌ها مطابقت ندارد. برای همگام‌سازی رمز عبور ثبت‌نام کنید.",
          authenticated: "تاییدشده",
          notRegistered: "ثبت‌نشده",
          select: "انتخاب",
          noCompanies: "شرکتی موجود نیست",
          logout: "خروج"
        },
        ar: {
          welcome: "مرحبًا",
          continueToAccount: "تابع للوصول إلى حسابك",
          phoneNumber: "رقم الهاتف",
          phoneNumberPlaceholder: "أدخل رقم هاتفك",
          password: "كلمة المرور",
          passwordPlaceholder: "أدخل كلمة المرور الخاصة بك",
          login: "تسجيل الدخول",
          forgotPassword: "هل نسيت كلمة المرور؟",
          noAccount: "ليس لديك حساب؟ ",
          register: "سجل",
          selectRole: "اختر الدور",
          selectRoleMessage: "لديك إمكانية الوصول إلى لوحات متعددة. الرجاء اختيار اللوحة التي تريد الوصول إليها:",
          customer: "لوحة العميل",
          employer: "لوحة صاحب العمل",
          admin: "لوحة المسؤول",
          cancel: "إلغاء"
        }
      }
    }
  },
  components: {
    NavbarStyledRegister,
  },
  computed: {
    availableCompanies() {
      return [...this.authenticatedCompanies, ...this.notAuthenticatedCompanies]
    }
  },
  mounted() {
    const savedLang = localStorage.getItem('selectedLanguage')
    if (savedLang) {
      this.currentLang = savedLang
    }
      const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    this.isDark = (savedTheme === 'dark')
  }
    this.applyLanguageSettings()
    this.checkCompanySelectionNeeded()
  },
  methods: {
    switchLanguage() {
      this.applyLanguageSettings()
      localStorage.setItem('selectedLanguage', this.currentLang)
    },
    
    applyLanguageSettings() {
      // Language settings logic
    },
    
    async checkCompanySelectionNeeded() {
      const authStore = useAuthStore()
      if (authStore.needsCompanySelection) {
        this.authenticatedCompanies = authStore.companies || []
        this.notAuthenticatedCompanies = []
        await this.openCompanySelection({
          roles: authStore.user?.roles || [],
          selected_company: null,
          authenticated_companies: this.authenticatedCompanies,
          not_authenticated_companies: [],
        })
      }
    },

    async openCompanySelection(loginData) {
      const loginAuthStore = useAuthStore()
      const loginRoles = (loginData?.roles || loginAuthStore.user?.roles || [])
        .map(role => String(role).toLowerCase())
      const currentActiveRole = String(loginAuthStore.selectedRole || loginAuthStore.user?.primaryRole || '').toLowerCase()
      if (loginRoles.includes('customer') || currentActiveRole === 'customer') {
        await this.$router.push('/select-company')
        return
      }

      this.showCompanySelection = true
      this.registrationCheckFailed = false
      this.registrationCheckError = ''
      this.pendingLoginData = loginData || null

      const authStore = useAuthStore()
      const roles = (loginData?.roles || authStore.user?.roles || [])
        .map(role => String(role).toLowerCase())
      const activeRole = String(authStore.selectedRole || authStore.user?.primaryRole || '').toLowerCase()
      if (!roles.includes('customer') && activeRole !== 'customer') return

      this.registrationCheckLoading = true
      try {
        const response = await authService.getCustomerRegistrationStatus()
        this.customerRegistrationProfile = response.data.profile || null
        if (!response.data.registration_complete) {
          this.showCustomerRegistrationModal = true
        }
      } catch (error) {
        console.error('Could not check customer registration on company selection:', error)
        this.registrationCheckFailed = true
        this.registrationCheckError = error.response?.data?.detail
          || 'امکان بررسی تکمیل اطلاعات حساب وجود ندارد. لطفاً دوباره تلاش کنید.'
      } finally {
        this.registrationCheckLoading = false
      }
    },

    async retryRegistrationCheck() {
      await this.openCompanySelection(this.pendingLoginData)
    },
    
    getRoleDisplayName(role) {
      const roleNames = {
        'customer': this.translations[this.currentLang].customer,
        'employer': this.translations[this.currentLang].employer,
        'admin': this.translations[this.currentLang].admin
      }
      return roleNames[role] || role
    },
    
    async handleLogin() {
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const authStore = useAuthStore()
        const response = await authStore.login(this.loginData.phoneNumber, this.loginData.password)
        
        console.log('Login response:', response)
        
        let loginData = response

        if (authStore.loginResponse) {
          loginData = authStore.loginResponse
        } else if (response.loginData) {
          loginData = response.loginData
        } else if (response.data) {
          loginData = response.data
        }

        loginData = {
          ...loginData,
          roles: loginData.roles || authStore.user?.roles || [],
          selected_company: loginData.selected_company !== undefined
            ? loginData.selected_company
            : authStore.selectedCompany,
          authenticated_companies: loginData.authenticated_companies || [],
          not_authenticated_companies: loginData.not_authenticated_companies || [],
        }

        console.log('Login data:', loginData)

        await this.continueLoginFlow(loginData)

      } catch (error) {
        console.error('Login error:', error)
        this.handleLoginError(error)
      } finally {
        this.loading = false
      }
    },

    async onCustomerRegistrationCompleted(result) {
      const loginData = this.pendingLoginData
      this.pendingLoginData = null
      this.customerRegistrationProfile = result?.profile || null
      this.showCustomerRegistrationModal = false

      try {
        // Registration was opened from the company-selection screen, which
        // stays visible behind the required modal. Selection can now continue.
        if (loginData && !this.showCompanySelection) await this.continueLoginFlow(loginData)
      } catch (error) {
        console.error('Customer registration completed, but continuing login failed:', error)
        this.errorMessage = error.response?.data?.detail
          || error.response?.data?.message
          || 'اطلاعات ثبت شد، اما ادامه ورود با خطا روبه‌رو شد. لطفاً دوباره تلاش کنید.'
      }
    },

    async continueLoginFlow(loginData) {
        const authStore = useAuthStore()
        const intendedCompanyId = String(
          this.$route.query.companyId || localStorage.getItem('intendedCompany') || ''
        ).trim()

        if (intendedCompanyId) {
          localStorage.removeItem('intendedCompany')
          const availableCompanies = loginData.companies || authStore.companies || []
          const intendedCompany = availableCompanies.find(company =>
            String(company.company_id) === intendedCompanyId &&
            company.company_type !== 'public_store'
          )
          const alreadySelected = String(loginData.selected_company?.company_id || '') === intendedCompanyId

          if (intendedCompany && intendedCompany.has_db_access !== false) {
            await this.selectCompany(intendedCompany)
            return
          }

          if (!alreadySelected) {
            this.errorMessage = 'این شرکت در فهرست شرکت‌های قابل دسترس حساب شما نیست. می‌توانید یکی از شرکت‌های موجود را انتخاب کنید.'
            if (!loginData.selected_company) {
              await this.$router.push('/select-company')
              return
            }
          }
        }

        if (loginData.selected_company) {
          //alert('Company already selected - auto-login')
          // No company selection needed
          if (loginData.roles && loginData.roles.length > 1) {
            this.availableRoles = loginData.roles
            this.showRoleSelection = true
          } else {
            this.redirectBasedOnRole()
          }
        } else {
          //alert('No company selected - needs selection')
          // Show company selection
          this.authenticatedCompanies = loginData.authenticated_companies || []
          this.notAuthenticatedCompanies = loginData.not_authenticated_companies || []
          
          const totalCompanies = [...this.authenticatedCompanies, ...this.notAuthenticatedCompanies]
          
          if (totalCompanies.length > 0) {
            await this.openCompanySelection(loginData)
          } else if (loginData.roles && loginData.roles.length > 1) {
            this.availableRoles = loginData.roles
            this.showRoleSelection = true
          } else {
            this.redirectBasedOnRole()
          }
        }
    },

    async handleIntendedCompany() {
      this.autoSelectInProgress = true
      
      try {
        const authStore = useAuthStore()
        const autoSelectResult = await authStore.handleIntendedCompany()
        
        if (autoSelectResult.autoSelected) {
          // Successfully auto-selected, redirect to company page
          //const companyName = autoSelectResult.company.company_name
         // const urlFriendlyName = companyName.replace(/\s+/g, '-').replace(/_/g, '-').toLowerCase()
          this.$router.push(`/dashboard`)
          return true
        } else {
          // Auto-select failed, show appropriate message
          console.log('Auto-select failed:', autoSelectResult.reason)
          
          if (autoSelectResult.reason === 'no_access') {
            this.errorMessage = 'شما به این شرکت دسترسی ندارید'
          } else if (autoSelectResult.reason === 'no_db_access') {
            this.errorMessage = 'این شرکت دسترسی به پایگاه داده ندارد'
          }
          
          // Fall back to normal company selection
          this.authenticatedCompanies = authStore.companies || []
          await this.openCompanySelection({
            roles: authStore.user?.roles || [],
            selected_company: null,
            authenticated_companies: this.authenticatedCompanies,
            not_authenticated_companies: [],
          })
          return false
        }
      } catch (error) {
        console.error('Error in auto-select:', error)
        return false
      } finally {
        this.autoSelectInProgress = false
      }
    },
    
    async selectCompany(company, selectedSubcode = null) {
      if (this.registrationCheckLoading || this.registrationCheckFailed || this.showCustomerRegistrationModal) return
      this.loadingCompanies = true
      try {
        console.log('Selecting company:', company)
        
        const authStore = useAuthStore()
        
        // If company has no DB access, we can't proceed
        if (!company.has_db_access) {
          this.errorMessage = 'این شرکت دسترسی به پایگاه داده ندارد'
          return
        }
        
        // Use the auth store's selectCompany method
        let selectionResponse
        if (authStore.selectCompany) {
          selectionResponse = await authStore.selectCompany(company.company_id, company.company_name, selectedSubcode)
        } else {
          // Fallback: Call API directly and update store
          const response = await authService.selectCompany(company.company_id, selectedSubcode)
          console.log('Company selection API response:', response.data)

          if (response.data?.requires_parent_selection) {
            selectionResponse = {
              requires_parent_selection: true,
              parent_matches: response.data.parent_matches || []
            }
          }
          
          // Update store manually
          if (response.data.success && response.data.access_token) {
            authStore.token = response.data.access_token
            localStorage.setItem('token', response.data.access_token)
            
            // Update user data
            if (authStore.user) {
              authStore.user = {
                ...authStore.user,
                subCodeId: response.data.subcode,
                firstName: response.data.first_name || authStore.user.firstName
              }
              localStorage.setItem('user', JSON.stringify(authStore.user))
            }
            
            // Update selected company
            if (response.data.selected_company) {
              authStore.selectedCompany = response.data.selected_company
              localStorage.setItem('selectedCompany', JSON.stringify(response.data.selected_company))
            }
            
            // Update companies list
            if (response.data.companies) {
              authStore.companies = response.data.companies
              localStorage.setItem('companies', JSON.stringify(response.data.companies))
            }
          }
        }

        if (selectionResponse?.requires_parent_selection) {
          this.pendingParentCompany = company
          this.parentSubcodeMatches = selectionResponse.parent_matches || []
          this.selectedParentSubcode = ''
          this.parentSubcodeSelectionOpen = true
          return
        }
        
        // Hide company selection modal
        this.showCompanySelection = false
        
        // Check roles after company selection
        if (authStore.hasMultipleRoles) {
          this.availableRoles = authStore.getAvailableRoles
          this.showRoleSelection = true
        } else {
          this.redirectBasedOnRole()
        }
      } catch (error) {
        console.error('Company selection error:', error)
        this.errorMessage = 'خطا در انتخاب شرکت'
        this.handleLoginError(error)
      } finally {
        this.loadingCompanies = false
      }
    },

    cancelParentSubcodeSelection() {
      this.parentSubcodeSelectionOpen = false
      this.parentSubcodeMatches = []
      this.selectedParentSubcode = ''
      this.pendingParentCompany = null
    },

    async continueWithParentSubcode() {
      if (!this.pendingParentCompany || !this.selectedParentSubcode) return
      const company = this.pendingParentCompany
      const subcode = this.selectedParentSubcode
      this.parentSubcodeSelectionOpen = false
      await this.selectCompany(company, subcode)
    },

    formatCompanies(companies, isAuthenticated) {
      if (!companies || companies.length === 0) return []
      
      // If companies are already objects, just add is_authenticated flag
      if (typeof companies[0] === 'object' && companies[0] !== null) {
        return companies.map(company => ({
          ...company,
          is_authenticated: isAuthenticated
        }))
      }
      
      // If companies are just IDs (backward compatibility)
      return companies.map(companyId => ({
        company_id: companyId,
        company_name: `Company ${companyId}`,
        description: '',
        has_db_access: true,
        is_authenticated: isAuthenticated
      }))
    },

    registerCompany(company) {
      if (this.registrationCheckLoading || this.registrationCheckFailed || this.showCustomerRegistrationModal) return
      // Navigate to the register company phone verification page
      this.$router.push({
        path: '/register-company-phone',
        query: { 
          companyId: company.company_id,
          companyName: company.company_name 
        }
      })
    },
    
    selectRole(role) {
      console.log('Selected role:', role)
      const authStore = useAuthStore()
      authStore.setSelectedRole(role)
      this.showRoleSelection = false
      this.redirectBasedOnRole()
    },
    
    redirectBasedOnRole() {
      const authStore = useAuthStore()
      const currentRole = authStore.getCurrentRole
      
      console.log('Redirecting based on role:', currentRole)
      
      switch(currentRole) {
        case 'customer':
          this.$router.push('/dashboard')
          break
        case 'employer':
          this.$router.push('/employer/dashboard')
          break
        case 'admin':
          this.$router.push('/admin/dashboard')
          break
        default:
          this.$router.push('/dashboard')
      }
    },
    
    logoutAndReset() {
      const authStore = useAuthStore()
      authStore.logout()
      this.showCompanySelection = false
      this.showRoleSelection = false
      this.availableCompanies = []
      this.availableRoles = []
      this.loginData = {
        phoneNumber: '',
        password: ''
      }
    },
    
    handleLoginError(error) {
      console.log('Login error details:', error)
      
      if (error.response) {
        const { data, status } = error.response
        console.log('Error response data:', data)
        
        if (status === 401) {
          if (data.detail?.includes('Phone number') || data.message?.includes('Phone number')) {
            this.fieldErrors.phoneNumber = 'شماره موبایل ثبت نشده است'
          } else if (data.detail?.includes('password') || data.message?.includes('password')) {
            this.fieldErrors.password = 'رمز عبور نامعتبر است'
          } else {
            this.errorMessage = data.detail || data.message || 'شماره موبایل یا رمز عبور نامعتبر است'
          }
        } else if (status === 400) {
          this.errorMessage = data.detail || data.message || 'شماره موبایل یا رمز عبور نامعتبر است'
        } else if (status === 404) {
          this.errorMessage = data.detail || 'شماره موبایل یافت نشد'
        } else if (status === 500) {
          this.errorMessage = 'خطا در سرور. لطفا بعدا تلاش کنید'
        } else {
          this.errorMessage = data.detail || data.message || 'خطا در ارتباط با سرور'
        }
      } else if (error.request) {
        this.errorMessage = 'خطا در ارتباط با سرور. لطفا اتصال اینترنت خود را بررسی کنید'
      } else {
        this.errorMessage = error.message || 'خطای ناشناخته رخ داده است'
      }
    }
  }
}
</script>
<style scoped>


/* ================= LOGIN LAYOUT ================= */

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 1rem;
  background: var(--bg);
}

.login-card {
  background: transparent !important;
  backdrop-filter: blur(4px); 
  color: var(--text);
  border-radius: 10px;
  padding: 2.5rem;
  box-shadow: 0 0 20px rgba(0,0,0,0.08);
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.login-card h2 {
  font-size: 1.8rem;
  margin-bottom: .5rem;
  color: var(--text);
}

.subtitle {
  font-size: .95rem;
  color: var(--subtext);
  margin-bottom: 1.5rem;
}

/* ================= FORM ================= */

.form-group {
  margin-bottom: 1.5rem;
  text-align: start;
}

.form-label {
  display: block;
  margin-bottom: .5rem;
  font-weight: 500;
  color: var(--text);
}

.form-input1 {
  width: 100%;
  padding: .75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 1rem;
  background: var(--card-bg);
  color: var(--text);
}

.form-input1:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(37,99,235,.15);
}

/* ================= BUTTONS ================= */

.login-btn {
  width: 100%;
  background: var(--primary);
  color: white;
  border: none;
  padding: .75rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;
  margin-top: .5rem;
}

.login-btn:hover:not(:disabled) {
  background: var(--primary-hover);
}

.login-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary);
  color: white;
  border: none;
  padding: .5rem 1.2rem;
  border-radius: 5px;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
  border: none;
  padding: .5rem 1rem;
  border-radius: 4px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
  padding: .5rem 1.2rem;
  border-radius: 4px;
}

.btn-sm {
  padding: .25rem .7rem;
  font-size: .85rem;
}

/* ================= LINKS ================= */

.forgot-password-link {
  text-align: center;
  margin-top: 1rem;
}

.forgot-password-link a,
.register-link a {
  color: var(--primary);
  text-decoration: none;
}

.forgot-password-link a:hover,
.register-link a:hover {
  text-decoration: underline;
}

.register-link {
  margin-top: 1.5rem;
  font-size: .9rem;
  color: var(--subtext);
  border-top: 1px solid var(--border);
  padding-top: 1.5rem;
}

/* ================= ALERTS ================= */

.text-danger {
  color: #dc2626;
  font-size: .8rem;
}

.text-success {
  color: #16a34a;
  font-size: .8rem;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: .75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: .75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

/* ================= MODALS ================= */

.role-selection-modal,
.company-selection-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.role-selection-content,
.company-selection-content {
  background: var(--card-bg);
  color: var(--text);
  border-radius: 10px;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
}

.company-selection-content {
  max-height: 80vh;
  overflow-y: auto;
}

.parent-subcode-overlay {
  position: fixed;
  z-index: 1010;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, .58);
  backdrop-filter: blur(4px);
}

.parent-subcode-dialog {
  width: min(100%, 480px);
  max-height: 82vh;
  overflow-y: auto;
  padding: 24px;
  border-radius: 16px;
  background: var(--card-bg, #fff);
  color: var(--text, #172033);
  box-shadow: 0 24px 70px rgba(15, 23, 42, .25);
}

.parent-subcode-dialog > p { color: var(--subtext, #64748b); line-height: 1.8; }
.parent-subcode-option { display: flex; gap: 12px; align-items: flex-start; margin: 10px 0; padding: 13px; border: 1px solid var(--border, #dbe2ea); border-radius: 11px; cursor: pointer; }
.parent-subcode-option input { margin-top: 4px; accent-color: var(--primary, #4f46e5); }
.parent-subcode-option span { display: grid; gap: 4px; }
.parent-subcode-option small { color: var(--subtext, #64748b); }
.parent-subcode-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.parent-subcode-actions button { min-width: 100px; }

@media (max-width: 520px) {
  .parent-subcode-actions { flex-direction: column-reverse; }
  .parent-subcode-actions button { width: 100%; }
}

/* ================= COMPANY LIST ================= */

.company-section {
  margin-bottom: 2rem;
}

.company-section-title {
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.company-section-subtitle {
  font-size: .9rem;
  color: var(--subtext);
}

.company-options {
  margin-top: 1rem;
}

.company-option {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: .8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: .25s;
}

.company-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0,0,0,.08);
}

.company-option.authenticated {
  border-left: 4px solid #28a745;
}

.company-option.not-authenticated {
  border-left: 4px solid #ffc107;
}

.company-info h4 {
  margin: 0 0 .4rem 0;
}

.company-description {
  font-size: .9rem;
  color: var(--subtext);
}

.company-status-badge {
  padding: .25rem .7rem;
  border-radius: 20px;
  font-size: .8rem;
}

.company-status-badge.authenticated {
  background: #d4edda;
  color: #155724;
}

.company-status-badge.not-authenticated {
  background: #fff3cd;
  color: #856404;
}

/* ================= MISC ================= */

.no-companies {
  text-align: center;
  padding: 2rem;
  color: var(--subtext);
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* ================= REGISTER-STYLE LOGIN SHELL ================= */

.login-page {
  position: relative;
  width: 100%;
  min-height: 100svh;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: 'Vazirmatn', Tahoma, 'Segoe UI', sans-serif;
  color: var(--text-primary, var(--text));
  background: var(--page-background, var(--bg));
}

.login-page *,
.login-page *::before,
.login-page *::after {
  box-sizing: border-box;
}

.login-page .login-container {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  align-items: center;
  width: 100%;
  min-height: 100svh;
  padding: 104px clamp(24px, 5vw, 88px) 32px;
  gap: clamp(24px, 4vw, 72px);
  background: transparent;
}

.login-page .login-form-col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
}

.login-page .login-card {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: min(100%, 400px);
  min-height: 525px;
  max-width: 400px;
  padding: 36px 32px;
  border: 1px solid var(--border, rgba(228, 228, 228, 0.18));
  border-radius: 20px;
  background: transparent !important;
  color: var(--text-primary, var(--text));
  box-shadow: 0 20px 50px var(--card-shadow, rgba(0, 0, 0, 0.25));
  outline: 1px solid var(--glass-border, rgba(237, 244, 240, 0.12));
  outline-offset: -1px;
  align-items: center;
  text-align: center;
}

.login-page .brand-mark {
  width: 100%;
  margin-bottom: 20px;
  text-align: center;
}

.login-page .brand-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent, var(--primary));
  box-shadow: 0 0 16px var(--chart-glow, rgba(240, 123, 50, 0.2));
}

.login-page .login-card h2 {
  width: 100%;
  margin: 0 0 8px;
  color: var(--text-primary, var(--text));
  font-size: clamp(20px, 4vw, 26px);
  font-weight: 800;
  line-height: 1.2;
}

.login-page .login-card .subtitle {
  width: 100%;
  margin: 0 0 28px;
  color: var(--text-muted, var(--subtext));
  font-size: 14px;
  line-height: 1.7;
}

.login-page .login-card form {
  width: 100%;
}

.login-page .form-group {
  display: grid;
  gap: 6px;
  text-align: center;
  margin-bottom: 16px;
}

.login-page .form-label {
  margin-bottom: 0;
  color: var(--text-muted, var(--subtext));
  font-size: 13px;
  font-weight: 600;
}

.login-page .form-input1 {
  width: 100%;
  text-align: center;
  min-height: 46px;
  padding: 11px 14px;
  border: 1px solid var(--control-border, var(--border));
  border-radius: 10px;
  background: var(--control-background, var(--card-bg));
  color: var(--text-primary, var(--text));
  font: inherit;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.login-page .form-input1::placeholder {
  color: var(--text-muted, var(--subtext));
}

.login-page .form-input1:focus {
  border-color: var(--accent, var(--primary));
  outline: none;
  box-shadow: 0 0 0 3px var(--chart-glow, rgba(240, 123, 50, 0.2));
}

.login-page .login-btn {
  min-height: 46px;
  margin-top: 8px;
  border: 0;
  border-radius: 10px;
  background: var(--accent, var(--primary));
  box-shadow: 0 8px 24px var(--chart-glow, rgba(240, 123, 50, 0.2));
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  transition: transform 0.18s, opacity 0.18s, box-shadow 0.18s;
}

.login-page .login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  background: var(--accent-soft, var(--accent));
  box-shadow: 0 12px 32px var(--chart-glow, rgba(240, 123, 50, 0.2));
}

.login-page .login-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.login-page .forgot-password-link {
  margin-top: 18px;
  text-align: center;
}

.login-page .forgot-password-link a,
.login-page .register-link a {
  color: var(--accent-soft, var(--primary));
}

.login-page .register-link {
  width: 100%;
  margin-top: auto;
  padding-top: 20px;
  border-top-color: var(--border, rgba(228, 228, 228, 0.18));
  color: var(--text-muted, var(--subtext));
  font-size: 13px;
  text-align: center;
}

.login-page .text-danger {
  margin-bottom: 10px;
  color: #e07070;
}

.login-page .text-success {
  margin-bottom: 10px;
  color: #72d6a0;
}

.login-page .login-image-col {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  height: min(72svh, 680px);
  overflow: hidden;
}

.login-page .login-visual {
  position: relative;
  display: block;
  width: min(100%, 480px);
  height: 480px;
  aspect-ratio: 1;
  object-fit: contain;
}

@media (min-width: 768px) {
  .login-page .login-visual {
    width: min(100%, 680px);
  }
}

.login-page .role-selection-content,
.login-page .company-selection-content {
  border: 1px solid var(--border, rgba(228, 228, 228, 0.18));
  background: var(--card-background, var(--card-bg));
  color: var(--text-primary, var(--text));
  box-shadow: 0 20px 50px var(--card-shadow, rgba(0, 0, 0, 0.25));
}

@media (max-width: 767px) {
  .login-page .login-container {
    position: relative;
    display: block;
    min-height: 100svh;
    padding: 82px 14px 28px;
  }

  .login-page .login-form-col {
    position: relative;
    z-index: 3;
    min-height: calc(100svh - 110px);
    align-items: center;
    justify-content: center;
  }

  .login-page .login-card {
    width: min(100%, 400px);
    padding: 28px 22px;
    background: transparent !important;
    border-color: color-mix(in srgb, var(--border, #ddd) 86%, var(--text-primary, #fff) 14%);
    box-shadow: 0 18px 44px color-mix(in srgb, var(--card-shadow, #000) 82%, transparent);
  }

  .login-page .login-image-col {
    position: absolute;
    z-index: 0;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .login-page .login-visual {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(112vw, 440px);
    height: 480px;
    max-width: 100%;
    transform: translate(-50%, -50%);
  }

  .login-page .register-link {
    margin-top: 34px;
  }
}

/* Keep the light-specific illustration colors intact on bright backgrounds. */
.login-page.theme-light .login-visual {
  mix-blend-mode: normal;
}
.password-group .input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.toggle-password {
  position: absolute;
  right: 10px; /* برای RTL باید از left: 10px استفاده کنید یا از منطق CSS جهت‌دار */
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #666;
}

/* اگر پروژه شما RTL است، جهت آیکون را اصلاح کنید */
[dir="rtl"] .toggle-password {
  right: auto;
  left: 10px;
}
/* ================= تعریف رنگ‌های تم روشن و تاریک صفحه لاگین ================= */

/* تم روشن */
.login-page.theme-light {
  background: #f1f5f9;
}

.login-page.theme-light .login-card1 {
  background: rgba(255, 255, 255, 0.069) !important;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05) !important;
  outline: none !important;
}

.login-page.theme-light .form-input1 {
  background-color: red !important;
  color: #1e293b !important;
  border: 1px solid #cbd5e1 !important;
}

.login-page.theme-light .form-input1::placeholder {
  color: #94a3b8 !important;
}

.login-page.theme-light .form-label,
.login-page.theme-light h2,
.login-page.theme-light .subtitle {
  color: #1e293b !important;
}


/* تم تاریک */
.login-page.theme-dark {
  background: #0f172a;
}

.login-page.theme-dark .login-card {
  background: rgba(255, 255, 255, 0.04) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3) !important;
  outline: none !important;
}



.login-page.theme-dark .form-label,
.login-page.theme-dark h2,
.login-page.theme-dark .subtitle {
  color: #f8fafc !important;
}


/* ================= استایل عمومی اینپوت و کارت ================= */

.login-page .login-card {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: min(100%, 400px);
  min-height: auto;
  max-width: 400px;
  padding: 36px 32px;
  border-radius: 20px;
  align-items: center;
  text-align: center;
  background: transparent;
}

.login-page .form-input1 {
  width: 100%;
  text-align: center;
  min-height: 46px;
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s ease-in-out;
}

.login-page .form-input1:focus {
  border-color: #2563eb !important;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2) !important;
}
/* Light theme inputs */
.login-page.theme-light .form-input1 {
  background-color: #ffffff !important;
  color: #1e293b !important;
  border: 1px solid #cbd5e1 !important;
}

.login-page.theme-light .form-input1::placeholder {
  color: #94a3b8 !important;
}

/* Dark theme inputs */
.login-page.theme-dark .form-input1 {
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: #f8fafc !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
}

.login-page.theme-dark .form-input1::placeholder {
  color: #94a3b8 !important;
}

.login-page.theme-dark .form-input1:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.35) !important;
}
</style>
